import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sparkle, Heart, Store, Wand2, ArrowRight, X, Mic } from "lucide-react";

const STEPS = [
  {
    icon: Heart, title: "Welcome to your Estate",
    body: "I'm your GIFTED concierge. Together we'll make sure no one you love is ever forgotten — from a first hello to a voice they'll keep forever. Let me show you around.",
    cta: "Begin the tour", to: null,
  },
  {
    icon: Store, title: "Curate your Boutique",
    body: "Your Personal Boutique captures the aesthetics and vibes you adore — the quiet intelligence behind every perfect recommendation.",
    cta: "Continue", to: null,
  },
  {
    icon: Wand2, title: "Meet the Magic Wand",
    body: "Whenever you write a card, tap the golden Magic Wand for bespoke message ideas — tuned to each person's taste. Pure thoughtfulness, in a click.",
    cta: "Continue", to: null,
  },
  {
    icon: Mic, title: "Add your Royal Voice",
    body: "In the Design Studio, tap the microphone to record a 30-second voice greeting. Royal members seal it into an Audio QR on the physical card — a keepsake they'll replay for years.",
    cta: "Open the Design Studio", to: "/cards",
  },
];

const SparkTrail = ({ accent = true }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {Array.from({ length: 10 }).map((_, i) => (
      <span key={i} className="spark" style={{
        left: `${8 + Math.random() * 84}%`,
        bottom: `${Math.random() * 40}%`,
        animationDelay: `${(i * 0.22).toFixed(2)}s`,
        transform: `scale(${0.6 + Math.random() * 0.8})`,
      }} />
    ))}
  </div>
);

const Onboarding = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Show only for signed-in users who haven't completed onboarding.
  if (!user || user.onboarding_completed || dismissed) return null;

  const finish = async (goto) => {
    setDismissed(true);
    try { await api.post("/onboarding/complete"); await refreshUser?.(); } catch (e) { console.error("Onboarding completion sync failed:", e); }
    if (goto) navigate(goto);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[hsl(38_30%_12%_/_0.5)] backdrop-blur-md" data-testid="onboarding-overlay">
      <div className="relative surface rounded-[2rem] w-full max-w-md p-8 text-center animate-fade-up overflow-hidden">
        <SparkTrail />
        <button data-testid="onboarding-skip" onClick={() => finish(null)}
          className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 z-10">
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-10">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-5 gold-pulse-ring">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <Sparkle className="w-4 h-4 gold-text" />
            <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Concierge</span>
          </div>
          <h2 className="font-serif text-3xl mb-3 shimmer-gold">{s.title}</h2>
          <p className="text-muted-foreground leading-relaxed mb-7">{s.body}</p>

          {/* step dots */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-primary" : "w-1.5 bg-muted"}`} />
            ))}
          </div>

          <button
            data-testid="onboarding-cta"
            onClick={() => {
              if (isLast) { finish(s.to || null); return; }  // final step → Design Studio (microphone)
              setStep((x) => x + 1);
            }}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90"
          >
            {s.cta} <ArrowRight className="w-4 h-4" />
          </button>
          {!isLast && (
            <button data-testid="onboarding-skip-tour" onClick={() => finish(null)} className="mt-3 text-xs text-muted-foreground hover:text-foreground">Skip tour</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
